<template>
  <div class="paginated-list-view">
    <form v-if="showSearch" class="search-row">
      <input type="text" v-model="searchTextRef" class="form-control form-control-sm" :placeholder="searchPlaceholder"/>
      <button type="submit" class="btn btn-primary btn-sm" :disabled="!searchTextValidRef" @click.prevent="reloadAsync">Search</button>
    </form>
    <div v-if="sortOptions" class="options">
      <div class="btn-group btn-group-sm" role="group" aria-label="Sort order">
        <template v-for="s in sortOptions" :key="s.value">
          <input type="radio" class="btn-check" name="sort" :id="'sort' + s.value" autocomplete="off" :value="s.value" v-model="sortRef"/>
          <label class="btn btn-outline-secondary" :for="'sort' + s.value">{{ s.label }}</label>
        </template>
      </div>
    </div>
    <ul class="list-unstyled results">
      <li class="grid-row header-row">
        <div class="grid-cell text-end">#</div>
        <slot name="header"></slot>
      </li>
      <li v-if="loadingRef && !pagesRef.length" class="grid-row loading">
        <div class="grid-cell">
          <LoadingIndicator/>
        </div>
      </li>
      <li v-for="(item, i) in pageRef?.items" :key="getRowKey(item, i)" class="grid-row">
        <div class="grid-cell rownum">{{ startRowIndexRef + i + 1 }}</div>
        <slot name="row" :item="item"></slot>
      </li>
    </ul>
    <ul class="list-unstyled pages">
      <li v-for="(p, i) in pagesRef" :key="i">
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="pageIndexRef === i" @click="goToPage(i)">{{ i + 1 }}</button>
      </li>
      <li>
        <button type="button" class="btn btn-primary btn-sm" :disabled="!pageRef?.nextPageToken" @click="nextPageAsync">Next</button>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.paginated-list-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-row {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;

  input {
    width: 400px;
    font-size: 0.875rem;
  }
}

.options {
  input[type="checkbox"] + label.btn-outline-secondary,
  input[type="radio"] + label.btn-outline-secondary {
    border-color: #ddd;
    color: #888;

    &:hover {
      background-color: #f4f4f4;
      color: black;
    }
  }

  input[type="checkbox"]:checked + label.btn-outline-secondary,
  input[type="radio"]:checked + label.btn-outline-secondary {
    background-color: #eee;
    color: black;

    &:hover {
      background-color: #e8e8e8;
      //color: var(--bs-primary);
    }
  }

  .btn.btn-outline-primary:not(:hover) img {
    filter: brightness(0) saturate(100%) invert(36%) sepia(54%) saturate(7072%) hue-rotate(211deg) brightness(104%) contrast(98%);
  }
}

ul.results {
  display: grid;
  width: fit-content;
  font-size: 0.75rem;

  .grid-row {
    display: contents;
    border-bottom: 1px solid #ddd;
    
    &.header-row {
      color: #aaa;
    }

    &:nth-child(even):not(.header-row):not(.loading) :deep(.grid-cell) {
      background-color: #f4f4f4;
    }

    &.loading .grid-cell {
      grid-column: 1 / -1;
      padding: 0.5rem 0;

      .loading-indicator {
        font-size: 0.75rem;
      }
    }

    :deep(.grid-cell) {
      position: relative;
      padding: 1px 0.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.rownum {
        text-align: end;
        color: #aaa;
      }
    }
  }
}

ul.pages {
  margin: 0;
  display: flex;
  gap: 0.5rem;
}
</style>

<script lang="ts" setup>
import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { PaginatedList } from './Api'
import { AsyncUtil } from './AsyncUtil'
import LoadingIndicator from './LoadingIndicator.vue'

interface SortOption {
  value: string
  label: string
}

const props = defineProps<{
  fetchPage: (params: { search?: string; sort: any; pageSize: number; next?: string; }) => Promise<PaginatedList<any>>
  getRowKey: (item: any, index: number) => string | number
  sortOptions?: SortOption[]
  searchPlaceholder?: string
  showSearch?: boolean
  pageSize?: number
}>()

const emit = defineEmits<{
  (e: 'sortChanged', sort?: string): void
}>()

const loadingRef = ref(false)
const searchTextRef = ref('')
const sortRef = ref(props.sortOptions?.at(0)?.value)
const pagesRef = ref([]) as Ref<PaginatedList<any>[]>
const pageIndexRef = ref(0)

const pageRef = computed(() => pagesRef.value.at(pageIndexRef.value))
const startRowIndexRef = computed(() => pagesRef.value.slice(0, pageIndexRef.value).reduce((acc, p) => acc + p.items.length, 0))
const trimmedSearchTextRef = computed(() => searchTextRef.value.trim())
const searchTextValidRef = computed(() => trimmedSearchTextRef.value == '' || trimmedSearchTextRef.value.length > 2)

onMounted(() => {
  if (props.sortOptions?.length) sortRef.value = props.sortOptions[0].value
  reloadAsync()
})

watch([sortRef], () => reloadAsync()) // add other settings that should trigger reload

watch(sortRef, () => emit('sortChanged', sortRef.value))

async function reloadAsync() {
  pagesRef.value = [] // clear results
  pagesRef.value = [await loadPageAsync()]
  pageIndexRef.value = 0
}

async function nextPageAsync() {
  const nextToken = pageRef.value?.nextPageToken
  if (nextToken) {
    const newIndex = pageIndexRef.value + 1
    if (newIndex === pagesRef.value.length) {
      const newPage = await loadPageAsync(nextToken)
      pagesRef.value.push(newPage)
    }
    pageIndexRef.value = newIndex
  }
}

async function loadPageAsync(nextToken?: string) {
  try {
    loadingRef.value = true
    const searchText = searchTextValidRef.value ? trimmedSearchTextRef.value : undefined
    return await props.fetchPage({ search: searchText, sort: sortRef.value, next: nextToken, pageSize: props.pageSize ?? 25 })
  }
  finally {
    loadingRef.value = false
  }
}

function goToPage(i: number) {
  pageIndexRef.value = i
}
</script>
